import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { school, whatsappLink } from "@/data/content";

const links = [
  { label: "Sobre", href: "#sobre" },
  { label: "Metodologia", href: "#metodologia" },
  { label: "Turmas", href: "#turmas" },
  { label: "Diferenciais", href: "#diferenciais" },
  { label: "Matrículas", href: "#matriculas" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl shadow-[0_8px_30px_rgb(30,58,95,0.06)] py-2" : "bg-white/40 backdrop-blur-md py-3"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 flex items-center justify-between">
        <a href="#topo" data-testid="logo-link" className="flex items-center gap-3">
          <img src={school.logo} alt="Recriar" className="h-12 w-12 md:h-14 md:w-14 object-contain" />
          <span className="hidden sm:block font-display font-600 text-ink leading-none text-lg">
            Instituto<br /><span className="text-brand-sky">Recriar</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="font-body font-600 text-ink-soft hover:text-ink relative after:absolute after:-bottom-1 after:left-0 after:h-[3px] after:w-0 after:rounded-full after:bg-brand-orange after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href={whatsappLink()}
          target="_blank"
          rel="noreferrer"
          data-testid="header-whatsapp-btn"
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-body font-700 text-white shadow-[0_6px_20px_rgb(78,205,196,0.4)] hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgb(78,205,196,0.5)] transition-transform duration-300"
        >
          <MessageCircle className="h-5 w-5" strokeWidth={2.5} /> WhatsApp
        </a>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="menu-toggle"
          className="lg:hidden rounded-full p-2.5 bg-ink text-white"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-ink/10 mt-2"
        >
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.slice(1)}`}
                className="font-body font-600 text-ink py-2 border-b border-ink/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3 font-700 text-white"
            >
              <MessageCircle className="h-5 w-5" /> Fale no WhatsApp
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
